import { useEffect, useRef } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilters } from '@refinedev/core';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

interface EmbeddedTableSearchProps {
  /** `setFilters` from the owning `useTable`. */
  setFilters: (filters: CrudFilters, behavior?: 'merge' | 'replace') => void;
  placeholder?: string;
}

/**
 * Debounced free-text search for tables embedded on show views.
 * Writes a single `q` filter (cleared when empty) on top of the
 * table's permanent filters.
 */
export const EmbeddedTableSearch = ({
  setFilters,
  placeholder,
}: EmbeddedTableSearchProps) => {
  const t = useTranslate();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const onChange = (value: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const q = value.trim();
      setFilters(
        [{ field: 'q', operator: 'eq', value: q === '' ? undefined : q }],
        'merge'
      );
    }, 300);
  };

  return (
    <Input
      allowClear
      prefix={<SearchOutlined />}
      placeholder={placeholder ?? t('common.search')}
      onChange={(e) => onChange(e.target.value)}
      style={{ maxWidth: 360 }}
    />
  );
};
